import { useTranslation } from 'react-i18next';

import { StatusDot } from '@/components/ui/StatusDot';

import type { StatusActionState } from './useStatusAction';

interface Props {
  state: StatusActionState;
}

/**
 * What the server said about a status action, rendered **beneath the block's upper strip and above the
 * interviews** (`DESIGN.md` §6.5, *The block's composition*).
 *
 * ⚠️ **It is deliberately not inside the control that caused it, and not a `Toast`.** These two
 * answers used to sit under the row of buttons; since TASK-125 the control is a **menu**, which closes
 * itself on select, so an answer rendered inside it would vanish at the moment it became relevant. A
 * `Toast` is for something that succeeded and needs no answer — a refused write is the opposite, and
 * §8's own split applies: an offline **write** gets the write sentence, which denies the queue outright
 * (BUG-017 — never §8's read banner, and never a promise that anything will be resent).
 *
 * Nothing here decides what to say: the sentence and the 409's field both arrive on
 * `StatusActionState`, so the two places that render an answer cannot disagree about which case it is.
 */
export function StatusActionAnswer({ state }: Props) {
  const { t } = useTranslation('candidates');
  const { failure, existingRun } = state;

  // §5's gate, for the reason `StatusActions` states: it travels with the component rather than living
  // only in the one caller that has it today.
  if (!state.mayAct) return null;

  if (!failure && !existingRun) return null;

  return (
    <div className="flex flex-col gap-2 px-4 pt-4">
      {failure && (
        <p role="alert" className="text-sm text-negative">
          {failure}
        </p>
      )}

      {/*
       * ⚠️ **The 409 says which run is in the way, and there is deliberately no link to it.** §6.4's
       * dialog links to `/candidates/{person_id}` for the same body, because it is on another screen;
       * here `existing_consideration` can only ever be **another run of this same person at this same
       * vacancy** (`perform_status_action` filters on both), and a consideration has no page of its
       * own (ADR 0002) — so the only page it could link to is this one. What a reader needs instead is
       * *which* block and *what to do*, which is what this says.
       */}
      {existingRun && (
        <div
          role="alert"
          className="flex flex-col items-start gap-2 rounded border border-rule-control bg-paper-sunken p-3"
        >
          <p className="text-sm text-ink-900">
            {t('card.actions.existingRun', { vacancy: existingRun.role_title })}
          </p>
          <StatusDot value={existingRun.status} />
        </div>
      )}
    </div>
  );
}
